export interface Collectible {
  type: 'coin' | 'gem';
  position: [number, number]; // [x, y] coordinates
  value: number;
  id: string;
}

export interface PuzzleMetadata {
  level: number;
  name?: string;
  difficulty: 'easy' | 'medium' | 'hard' | 'expert';
  theme: 'forest' | 'cave' | 'lava' | 'ice' | 'magical' | 'dungeon';
  collectibles: Collectible[];
  minStars: number; // Minimum stars to unlock
  timeTarget?: number; // Target completion time in seconds
  stepTarget?: number; // Target steps for 3 stars
}

export interface EnhancedPuzzle {
  grid: string[];
  metadata: PuzzleMetadata;
}

// Type that can be either legacy string[] or enhanced puzzle
export type PuzzleData = string[] | EnhancedPuzzle;

// Helper to check if puzzle is enhanced
export function isEnhancedPuzzle(puzzle: PuzzleData): puzzle is EnhancedPuzzle {
  return typeof puzzle === 'object' && 'grid' in puzzle && 'metadata' in puzzle;
}

// Helper to get grid from either format
export function getPuzzleGrid(puzzle: PuzzleData): string[] {
  return isEnhancedPuzzle(puzzle) ? puzzle.grid : puzzle;
}

// Helper to get collectibles from puzzle
export function getPuzzleCollectibles(puzzle: PuzzleData): Collectible[] {
  if (isEnhancedPuzzle(puzzle)) {
    return puzzle.metadata.collectibles;
  }
  
  // For legacy puzzles, auto-generate collectibles based on 'p' markers
  const collectibles: Collectible[] = [];
  const grid = puzzle;
  
  grid.forEach((row, y) => {
    row.split('').forEach((cell, x) => {
      if (cell.toLowerCase() === 'p') {
        collectibles.push({
          type: 'coin',
          position: [x, y],
          value: 10,
          id: `auto_${x}_${y}`
        });
      }
    });
  });
  
  return collectibles;
}

// Helper to get puzzle metadata with defaults
export function getPuzzleMetadata(puzzle: PuzzleData, levelIndex: number): PuzzleMetadata {
  if (isEnhancedPuzzle(puzzle)) {
    return puzzle.metadata;
  }
  
  // Generate default metadata for legacy puzzles
  const grid = puzzle;
  const totalCells = grid.reduce((sum, row) => sum + row.length, 0);
  
  // Determine difficulty based on grid size and complexity
  let difficulty: PuzzleMetadata['difficulty'] = 'easy';
  if (totalCells > 50) difficulty = 'medium';
  if (totalCells > 100) difficulty = 'hard';
  if (levelIndex > 40) difficulty = 'expert';
  
  // Determine theme based on obstacles
  const gridString = grid.join('').toLowerCase();
  let theme: PuzzleMetadata['theme'] = 'forest';
  if (gridString.includes('l')) theme = 'lava';
  if (gridString.includes('s')) theme = 'ice';
  if (levelIndex > 30) theme = 'magical';
  if (levelIndex > 20) theme = 'cave';
  
  return {
    level: levelIndex,
    difficulty,
    theme,
    collectibles: getPuzzleCollectibles(puzzle),
    minStars: 0,
    timeTarget: Math.max(30, totalCells * 2), // 2 seconds per cell
    stepTarget: Math.max(10, Math.floor(totalCells * 0.7)) // 70% of total cells
  };
}
